import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import styled, { ThemeProvider } from 'styled-components';
import { themeState } from 'styleguide/theme';
import { DisplayXS, TextLG } from 'styleguide/Texts';
import { Card } from 'styleguide/Card';
import { View } from 'styleguide/View';
import { FolderDetailViewModel } from './FolderDetailViewModel';
import { ShareableRoutineFolder } from 'hevy-shared';
import { sendEvent } from 'utils/events';
import { stores } from 'state/stores';
import { Spacing } from 'styleguide/spacing';
import { FolderOwnerContent } from './components/FolderOwnerContent';
import { FolderNotFoundView } from './components/FolderNotFound';
import { FolderDetailHevyCTACard } from './components/FolderDetailHevyCTACard';
import { HideOnPhoneWrapper, ShowOnPhoneWrapper } from 'styleguide/Breakpoints';
import { FlexRow } from 'styleguide/Row';
import { RoutineCell } from './components/RoutineCell';

interface FolderDetailScreenProps {
  folder?: ShareableRoutineFolder;
  isAuthenticated: boolean;
}

export const FolderDetailScreen = ({ folder, isAuthenticated }: FolderDetailScreenProps) => {
  const [vm] = useState(new FolderDetailViewModel({ folder, isAuthenticated }));

  useEffect(() => {
    setTimeout(() => {
      /**
       * Without setTimeout this event is fired before
       * amplitude has been initialized.
       */
      sendEvent('folderDetail_screenview', {
        authenticated: stores.auth.isAuthenticated,
      });
    });
  }, []);

  return <FolderDetailScreenInner vm={vm} />;
};

const ScreenContainer = styled(View)`
  flex: 1;
  overflow-y: auto;
  justify-content: center;
  align-items: center;
`;

const Container = styled(View)`
  flex: 1;
  width: 100%;
  max-width: 1048px;
  min-height: 90vh;
`;

const Content = styled(View)`
  flex-direction: row;
  justify-content: center;
  gap: ${Spacing.lg}px;
  margin: ${Spacing.lg}px 0px;
  padding: 0px ${Spacing.md}px ${Spacing.lg}px;
`;

const LeftContainer = styled(View)`
  flex: 684;
  @media (max-width: 820px) {
    min-width: 80vw;
  }
  max-width: 684px;
  gap: ${Spacing.md}px;
`;

const FolderHeaderContainer = styled(View)`
  padding: ${Spacing.lg}px;
`;

const RoutinesContainer = styled(View)`
  padding: 0px ${Spacing.md}px;
`;

const RightContainer = styled(HideOnPhoneWrapper)`
  display: flex;
  flex-direction: column;
  flex: 330;
  max-width: 330px;
  gap: ${Spacing.md}px;
`;

interface FolderDetailScreenInnerProps {
  vm: FolderDetailViewModel;
}

const FolderDetailScreenInner = observer(({ vm }: FolderDetailScreenInnerProps) => {
  return (
    <ThemeProvider theme={themeState.current}>
      <ScreenContainer>
        <Container>
          {vm.isError ? (
            <FolderNotFoundView />
          ) : (
            <Content>
              <LeftContainer>
                <Card>
                  <FolderHeaderContainer>
                    <FlexRow style={{ justifyContent: 'space-between' }}>
                      <DisplayXS>{vm.title}</DisplayXS>
                    </FlexRow>
                    <ShowOnPhoneWrapper>
                      <FolderOwnerContent vm={vm}></FolderOwnerContent>
                    </ShowOnPhoneWrapper>
                  </FolderHeaderContainer>

                  <RoutinesContainer>
                    {vm.folder && vm.isFolderEmpty ? (
                      <Content>
                        <TextLG>This folder is empty</TextLG>
                      </Content>
                    ) : (
                      vm.folder?.routines.map(routine => {
                        return (
                          <RoutineCell
                            key={routine.id}
                            title={routine.title}
                            notes={routine.notes}
                            style={{ marginBottom: Spacing.md }}
                            exercises={routine.exercises}
                          />
                        );
                      })
                    )}
                  </RoutinesContainer>
                </Card>
                <ShowOnPhoneWrapper>
                  {!vm.isAuthenticated && <FolderDetailHevyCTACard onCopyUrl={vm.onCopyUrl} />}
                </ShowOnPhoneWrapper>
              </LeftContainer>
              <RightContainer>
                <Card style={{ padding: Spacing.lg }}>
                  <FolderOwnerContent vm={vm}></FolderOwnerContent>
                </Card>
                {!vm.isAuthenticated && <FolderDetailHevyCTACard onCopyUrl={vm.onCopyUrl} />}
              </RightContainer>
            </Content>
          )}
        </Container>
      </ScreenContainer>
    </ThemeProvider>
  );
});
